import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, BookOpen, Check, ChevronDown, ChevronUp,
  Edit3, Globe, Loader2, Plus, RefreshCw, Sparkles, Trash2, Users, Wand2, X
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Slider } from "./ui/slider";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../lib/api";

const STEPS = [
  { id: 1, label: "The Spark", icon: Sparkles },
  { id: 2, label: "Foundation", icon: BookOpen },
  { id: 3, label: "Characters", icon: Users },
  { id: 4, label: "World", icon: Globe },
  { id: 5, label: "Scope", icon: Edit3 },
  { id: 6, label: "Chapters", icon: BookOpen },
  { id: 7, label: "Create", icon: Check },
];

const ARC_TYPES = [
  "Hero's Journey", "Rags to Riches", "Tragedy", "Rebirth",
  "Quest", "Voyage and Return", "Comedy", "Overcoming the Monster"
];

const StoryBuilderWizard = ({ userId, onCreated, onCancel, genres }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Step 1: The Spark
  const [premise, setPremise] = useState("");
  const [genreHint, setGenreHint] = useState("");

  // Step 2: Foundation
  const [foundation, setFoundation] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState(0);
  const [editingPitch, setEditingPitch] = useState(false);

  // Step 3: Characters
  const [characters, setCharacters] = useState([]);
  const [expandedChar, setExpandedChar] = useState(null);

  // Step 4: World
  const [worldBuilding, setWorldBuilding] = useState({});
  const [storyStructure, setStoryStructure] = useState({});
  const [keyRelationships, setKeyRelationships] = useState([]);

  // Step 5: Scope
  const [numChapters, setNumChapters] = useState(24);
  const [chapterLength, setChapterLength] = useState("medium");
  const [arcType, setArcType] = useState("Hero's Journey");

  // Step 6: Chapters
  const [chapters, setChapters] = useState([]);
  const [expandedChapter, setExpandedChapter] = useState(null);

  // Generate blueprint from premise
  const generateBlueprint = async () => {
    if (!premise || premise.length < 10) {
      toast.error("Tell me more about your story (at least a sentence)");
      return;
    }
    setLoading(true);
    setLoadingStep("Crafting your story's foundation...");
    setLoadingProgress(0);

    let pollInterval = null;
    let timeoutId = null;
    let isCompleted = false;

    try {
      const res = await axios.post(`${API}/story-builder/generate`, {
        premise,
        genre: genreHint || null
      });
      const jobId = res.data.job_id;

      pollInterval = setInterval(async () => {
        if (isCompleted) return;
        try {
          const status = await axios.get(`${API}/story-builder/generate/${jobId}`);
          const job = status.data;
          setLoadingStep(job.step);
          setLoadingProgress(job.progress);

          if (job.status === "completed" && job.result) {
            isCompleted = true;
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            const r = job.result;
            setFoundation(r.foundation);
            setSelectedTitle(0);
            setCharacters(r.characters || []);
            setWorldBuilding(r.world_building || {});
            setStoryStructure(r.story_structure || {});
            setKeyRelationships(r.key_relationships || []);
            setNumChapters(r.foundation?.suggested_chapter_count || 24);
            setArcType(r.foundation?.suggested_arc || "Hero's Journey");
            setStep(2);
            setLoading(false);
            toast.success("Your story blueprint is ready!");
          } else if (job.status === "failed") {
            isCompleted = true;
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            setLoading(false);
            toast.error(job.error || "Generation failed. Please try again.");
          }
        } catch {
          // keep polling
        }
      }, 2000);

      timeoutId = setTimeout(() => {
        if (!isCompleted) {
          isCompleted = true;
          clearInterval(pollInterval);
          setLoading(false);
          toast.error("Generation timed out. Please try again.");
        }
      }, 300000);
    } catch (err) {
      setLoading(false);
      toast.error("Failed to start generation");
    }
  };

  // Generate chapter outlines
  const generateOutlines = async () => {
    setLoading(true);
    setLoadingStep("Mapping your story arc...");
    setLoadingProgress(0);

    let pollInterval = null;
    let timeoutId = null;
    let isCompleted = false;

    try {
      const title = foundation?.titles?.[selectedTitle] || "Untitled";
      const res = await axios.post(`${API}/story-builder/generate-outlines`, {
        title,
        genre: foundation?.genre || "Fiction",
        elevator_pitch: foundation?.elevator_pitch || "",
        tone: foundation?.tone || "balanced",
        setting: foundation?.setting || "",
        themes: foundation?.themes || [],
        characters,
        world_building: worldBuilding,
        story_structure: storyStructure,
        num_chapters: numChapters,
        chapter_length: chapterLength,
        arc_type: arcType
      });
      const jobId = res.data.job_id;

      pollInterval = setInterval(async () => {
        if (isCompleted) return;
        try {
          const status = await axios.get(`${API}/story-builder/generate-outlines/${jobId}`);
          const job = status.data;
          setLoadingStep(job.step);
          setLoadingProgress(job.progress);

          if (job.status === "completed" && job.result) {
            isCompleted = true;
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            setChapters(job.result.chapters || []);
            setStep(6);
            setLoading(false);
            toast.success(`${(job.result.chapters || []).length} chapter outlines generated!`);
          } else if (job.status === "failed") {
            isCompleted = true;
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            setLoading(false);
            toast.error(job.error || "Outline generation failed.");
          }
        } catch {
          // keep polling
        }
      }, 2000);

      timeoutId = setTimeout(() => {
        if (!isCompleted) {
          isCompleted = true;
          clearInterval(pollInterval);
          setLoading(false);
          toast.error("Outline generation timed out.");
        }
      }, 300000);
    } catch (err) {
      setLoading(false);
      toast.error("Failed to start outline generation");
    }
  };

  // Create the actual project
  const handleCreateProject = async () => {
    setLoading(true);
    setLoadingStep("Creating your book...");
    setLoadingProgress(10);
    
    try {
      const title = foundation?.titles?.[selectedTitle] || "Untitled";
      const protagonists = characters.filter(c =>
        c.role === "protagonist" || c.role === "love_interest" || c.role === "mentor"
      ).map(c => ({
        name: c.name,
        description: c.brief_description || "",
        role: c.role,
        age: c.age?.toString() || "",
        motivation: c.motivation || ""
      }));

      setLoadingStep("Preparing story data...");
      setLoadingProgress(30);

      const allChars = characters.map(c => ({
        name: c.name,
        role: c.role,
        brief_description: c.brief_description || "",
        age: c.age,
        gender: c.gender,
        hair_color: c.physical?.hair_color || c.hair_color || "",
        eye_color: c.physical?.eye_color || c.eye_color || "",
        skin_tone: c.physical?.skin_tone || c.skin_tone || "",
        height: c.physical?.height || c.height || "",
        build: c.physical?.build || c.build || "",
        personality: c.personality || "",
        motivation: c.motivation || "",
        backstory: c.backstory || "",
        arc: c.arc || ""
      }));

      const chapterOutlines = chapters.map(ch => ({
        number: ch.number,
        title: ch.title,
        summary: ch.summary,
        key_beats: ch.key_beats || [],
        pov: ch.pov || "",
        setting: ch.setting || "",
        characters_present: ch.characters_present || [],
        tension_level: ch.tension_level || 5
      }));

      setLoadingStep("Building your story bible...");
      setLoadingProgress(50);

      const storyBible = {
        title,
        genre: foundation?.genre || "Fiction",
        elevator_pitch: foundation?.elevator_pitch || "",
        tone: foundation?.tone || "",
        themes: foundation?.themes || [],
        story_structure: storyStructure,
        world_building: worldBuilding,
        characters: allChars,
        canonical_names: {
          characters: allChars.map(c => c.name),
          locations: (worldBuilding?.locations || []).map(l => l.name || l)
        }
      };

      const projectData = {
        title,
        genre: foundation?.genre || "Fiction",
        target_length: numChapters <= 10 ? "novella" : numChapters <= 30 ? "novel" : "epic",
        num_chapters: numChapters,
        chapter_length: chapterLength === "short" ? 800 : chapterLength === "long" ? 2000 : 1200,
        protagonists: protagonists,
        story_bible: storyBible,
        chapter_outline: chapterOutlines,
        auto_generate_outline: false,
        complexity_level: "detailed",
        elevator_pitch: foundation?.elevator_pitch || "",
        tone: foundation?.tone || "balanced",
        setting: foundation?.setting || "",
        themes: foundation?.themes || []
      };

      setLoadingStep("Saving your book...");
      setLoadingProgress(70);

      const res = await axios.post(`${API}/projects?user_id=${userId}`, projectData);
      
      setLoadingStep("Success! Opening your book...");
      setLoadingProgress(100);
      
      toast.success("Your book has been created! Time to write!");
      
      // Small delay to show success state before navigation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      onCreated(res.data);
    } catch (err) {
      console.error("Project creation error:", err);
      console.error("Error response:", err?.response?.data);
      
      // Parse error message - handle Pydantic validation errors
      let msg = "Failed to create project";
      if (err?.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Pydantic validation error array
          msg = err.response.data.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
        } else {
          msg = err.response.data.detail;
        }
      } else if (err?.message) {
        msg = err.message;
      }
      
      toast.error(msg);
      setLoadingStep("Error occurred");
      setLoadingProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const updateCharacter = (idx, field, value) => {
    const updated = [...characters];
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      updated[idx] = { ...updated[idx], [parent]: { ...updated[idx][parent], [child]: value } };
    } else {
      updated[idx] = { ...updated[idx], [field]: value };
    }
    setCharacters(updated);
  };

  const removeCharacter = (idx) => {
    setCharacters(characters.filter((_, i) => i !== idx));
  };

  const addCharacter = () => {
    setCharacters([...characters, {
      name: "", role: "supporting", brief_description: "", age: "",
      gender: "", physical: {}, personality: "", motivation: "", backstory: "", arc: ""
    }]);
    setExpandedChar(characters.length);
  };

  const updateChapter = (idx, field, value) => {
    const updated = [...chapters];
    updated[idx] = { ...updated[idx], [field]: value };
    setChapters(updated);
  };

  // Progress bar component
  const ProgressBar = () => (
    <div className="flex items-center gap-1 mb-4 px-1" data-testid="builder-progress-bar">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const isActive = s.id === step;
        const isDone = s.id < step;
        return (
          <div key={s.id} className="flex items-center flex-1">
            <button
              onClick={() => isDone && setStep(s.id)}
              disabled={!isDone}
              className={`flex items-center justify-center w-7 h-7 rounded-full text-xs transition-all ${
                isActive ? "bg-cyan-600 text-white scale-110" :
                isDone ? "bg-cyan-600/30 text-cyan-400 cursor-pointer hover:bg-cyan-600/50" :
                "bg-slate-800 text-slate-400"
              }`}
              title={s.label}
            >
              {isDone ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
            </button>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 ${isDone ? "bg-cyan-600/50" : "bg-slate-800"}`} />
            )}
          </div>
        );
      })}
    </div>
  );

  // Loading overlay
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4" data-testid="builder-loading">
        <Loader2 className="w-10 h-10 text-cyan-500 animate-spin" />
        <p className="text-sm text-cyan-400 font-medium">{loadingStep}</p>
        <div className="w-48 h-2 bg-slate-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-cyan-600 to-purple-600 rounded-full"
            animate={{ width: `${loadingProgress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <p className="text-xs text-muted-foreground">{loadingProgress}% complete</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 overflow-y-auto max-h-[calc(90vh-120px)]">
      <ProgressBar />

      <AnimatePresence mode="wait">
        {/* STEP 1: THE SPARK */}
        {step === 1 && (
          <motion.div key="spark" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-foreground">What's your story about?</h3>
              <p className="text-xs text-muted-foreground mt-1">One sentence is all it takes. The more detail you give, the better the result.</p>
            </div>
            <Textarea
              value={premise}
              onChange={(e) => setPremise(e.target.value)}
              placeholder="A con artist falls in love with the woman he's supposed to scam, but she's running a bigger con than he is..."
              className="min-h-[100px] bg-slate-900/50 border-slate-700 text-sm resize-none"
              data-testid="builder-premise-input"
            />
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Genre hint (optional — AI will auto-detect)</label>
              <Select value={genreHint} onValueChange={setGenreHint}>
                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-sm" data-testid="builder-genre-hint">
                  <SelectValue placeholder="Let AI decide..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Let AI decide</SelectItem>
                  {(genres || []).map(g => {
                    const gId = typeof g === 'string' ? g : g.id;
                    const gName = typeof g === 'string' ? g : g.name;
                    return <SelectItem key={gId} value={gId}>{gName}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={generateBlueprint}
              disabled={!premise || premise.length < 10}
              className="w-full bg-cyan-600 hover:bg-cyan-700"
              data-testid="builder-generate-btn"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Build My Story
            </Button>
          </motion.div>
        )}

        {/* STEP 2: FOUNDATION */}
        {step === 2 && foundation && (
          <motion.div key="foundation" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Pick your title</h3>
            <div className="grid grid-cols-1 gap-2">
              {(foundation.titles || []).map((t, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedTitle(i)}
                  className={`text-left px-3 py-2 rounded-lg border transition-all text-sm ${
                    selectedTitle === i
                      ? "border-cyan-500 bg-cyan-600/10 text-cyan-300"
                      : "border-slate-700 bg-slate-900/30 text-foreground hover:border-slate-500"
                  }`}
                  data-testid={`builder-title-option-${i}`}
                >
                  {t}
                  {selectedTitle === i && <Check className="w-3.5 h-3.5 inline ml-2 text-cyan-400" />}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">Genre</label>
                <Input value={foundation.genre || ""} onChange={(e) => setFoundation({...foundation, genre: e.target.value})} className="bg-slate-900/50 border-slate-700 text-sm" data-testid="builder-genre" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Tone</label>
                <Input value={foundation.tone || ""} onChange={(e) => setFoundation({...foundation, tone: e.target.value})} className="bg-slate-900/50 border-slate-700 text-sm" data-testid="builder-tone" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs text-muted-foreground">Elevator Pitch</label>
                <button onClick={() => setEditingPitch(!editingPitch)} className="text-xs text-cyan-500 hover:text-cyan-400">
                  {editingPitch ? "Done" : "Edit"}
                </button>
              </div>
              {editingPitch ? (
                <Textarea value={foundation.elevator_pitch || ""} onChange={(e) => setFoundation({...foundation, elevator_pitch: e.target.value})} className="bg-slate-900/50 border-slate-700 text-sm min-h-[60px]" data-testid="builder-pitch-edit" />
              ) : (
                <p className="text-sm text-foreground bg-slate-900/30 rounded-lg p-2 border border-slate-700" data-testid="builder-pitch-display">{foundation.elevator_pitch}</p>
              )}
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Setting</label>
              <Input value={foundation.setting || ""} onChange={(e) => setFoundation({...foundation, setting: e.target.value})} className="bg-slate-900/50 border-slate-700 text-sm" data-testid="builder-setting" />
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Themes</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {(foundation.themes || []).map((t, i) => (
                  <Badge key={i} variant="secondary" className="text-xs cursor-pointer hover:bg-destructive/20" onClick={() => setFoundation({...foundation, themes: foundation.themes.filter((_, j) => j !== i)})}>
                    {t} <X className="w-2.5 h-2.5 ml-1" />
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="builder-back-btn">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
              <Button onClick={() => setStep(3)} className="flex-1 bg-cyan-600 hover:bg-cyan-700" data-testid="builder-next-btn">
                Characters <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* STEP 3: CHARACTERS */}
        {step === 3 && (
          <motion.div key="characters" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Your Cast ({characters.length})</h3>
              <Button variant="ghost" size="sm" onClick={addCharacter} className="text-xs text-cyan-500" data-testid="builder-add-character">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Character
              </Button>
            </div>

            <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
              {characters.map((char, idx) => (
                <Card key={idx} className="bg-slate-900/40 border-slate-700">
                  <CardContent className="p-2.5">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedChar(expandedChar === idx ? null : idx)}
                      data-testid={`builder-char-${idx}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          char.role === 'protagonist' ? 'bg-cyan-600/30 text-cyan-300' :
                          char.role === 'antagonist' ? 'bg-red-600/30 text-red-300' :
                          char.role === 'love_interest' ? 'bg-pink-600/30 text-pink-300' :
                          'bg-slate-700 text-slate-300'
                        }`}>
                          {(char.name || "?")[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{char.name || "Unnamed"}</p>
                          <Badge variant="outline" className="text-[10px]">{char.role}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={(e) => { e.stopPropagation(); removeCharacter(idx); }} className="text-slate-400 hover:text-red-400 p-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        {expandedChar === idx ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </div>

                    {expandedChar === idx && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="mt-3 space-y-2 border-t border-slate-700 pt-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-muted-foreground">Name</label>
                            <Input value={char.name || ""} onChange={(e) => updateCharacter(idx, "name", e.target.value)} className="bg-slate-800 border-slate-600 text-xs h-8" />
                          </div>
                          <div>
                            <label className="text-[10px] text-muted-foreground">Role</label>
                            <Select value={char.role} onValueChange={(v) => updateCharacter(idx, "role", v)}>
                              <SelectTrigger className="bg-slate-800 border-slate-600 text-xs h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {["protagonist", "antagonist", "love_interest", "mentor", "sidekick", "supporting", "family", "minor"].map(r => (
                                  <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-muted-foreground">Age</label>
                            <Input value={char.age || ""} onChange={(e) => updateCharacter(idx, "age", e.target.value)} className="bg-slate-800 border-slate-600 text-xs h-8" />
                          </div>
                          <div>
                            <label className="text-[10px] text-muted-foreground">Gender</label>
                            <Input value={char.gender || ""} onChange={(e) => updateCharacter(idx, "gender", e.target.value)} className="bg-slate-800 border-slate-600 text-xs h-8" />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">Description</label>
                          <Textarea value={char.brief_description || ""} onChange={(e) => updateCharacter(idx, "brief_description", e.target.value)} className="bg-slate-800 border-slate-600 text-xs min-h-[50px]" />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">Motivation</label>
                          <Input value={char.motivation || ""} onChange={(e) => updateCharacter(idx, "motivation", e.target.value)} className="bg-slate-800 border-slate-600 text-xs h-8" />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">Character Arc</label>
                          <Input value={char.arc || ""} onChange={(e) => updateCharacter(idx, "arc", e.target.value)} className="bg-slate-800 border-slate-600 text-xs h-8" />
                        </div>
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1" data-testid="builder-back-btn">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Foundation
              </Button>
              <Button onClick={() => setStep(4)} className="flex-1 bg-cyan-600 hover:bg-cyan-700" data-testid="builder-next-btn">
                World <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* STEP 4: WORLD & STRUCTURE */}
        {step === 4 && (
          <motion.div key="world" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Story Structure</h3>

            {["beginning", "middle", "ending"].map((part) => (
              <div key={part}>
                <label className="text-xs text-muted-foreground capitalize">{part === "beginning" ? "Act 1 — Beginning" : part === "middle" ? "Act 2 — Middle" : "Act 3 — Ending"}</label>
                <Textarea
                  value={storyStructure[part] || ""}
                  onChange={(e) => setStoryStructure({...storyStructure, [part]: e.target.value})}
                  className="bg-slate-900/50 border-slate-700 text-xs min-h-[50px]"
                  data-testid={`builder-structure-${part}`}
                />
              </div>
            ))}

            <h3 className="text-sm font-semibold text-foreground pt-2">Key Story Beats</h3>
            <div className="grid grid-cols-2 gap-2">
              {["inciting_incident", "midpoint_twist", "climax", "resolution"].map((beat) => (
                <div key={beat}>
                  <label className="text-[10px] text-muted-foreground">{beat.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</label>
                  <Textarea
                    value={storyStructure[beat] || ""}
                    onChange={(e) => setStoryStructure({...storyStructure, [beat]: e.target.value})}
                    className="bg-slate-900/50 border-slate-700 text-[11px] min-h-[40px]"
                  />
                </div>
              ))}
            </div>

            {worldBuilding?.locations?.length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-foreground pt-2">Key Locations</h3>
                <div className="space-y-1">
                  {worldBuilding.locations.map((loc, i) => (
                    <div key={i} className="bg-slate-900/30 rounded-lg p-2 border border-slate-700">
                      <p className="text-xs font-medium text-foreground">{loc.name || loc}</p>
                      {loc.description && <p className="text-[10px] text-muted-foreground">{loc.description}</p>}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(3)} className="flex-1" data-testid="builder-back-btn">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Characters
              </Button>
              <Button onClick={() => setStep(5)} className="flex-1 bg-cyan-600 hover:bg-cyan-700" data-testid="builder-next-btn">
                Scope <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* STEP 5: SCOPE */}
        {step === 5 && (
          <motion.div key="scope" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Story Scope</h3>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-muted-foreground">Number of Chapters</label>
                <span className="text-sm font-bold text-cyan-400">{numChapters}</span>
              </div>
              <Slider
                value={[numChapters]}
                onValueChange={([v]) => setNumChapters(v)}
                min={5}
                max={60}
                step={1}
                className="w-full"
                data-testid="builder-chapter-count-slider"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span>5 (Short)</span>
                <span>30 (Novel)</span>
                <span>60 (Epic)</span>
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-2 block">Chapter Length</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "short", label: "Short", desc: "600-800 words" },
                  { id: "medium", label: "Medium", desc: "1,000-1,500 words" },
                  { id: "long", label: "Long", desc: "1,800-2,500 words" },
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setChapterLength(opt.id)}
                    className={`px-3 py-2 rounded-lg border text-center transition-all ${
                      chapterLength === opt.id
                        ? "border-cyan-500 bg-cyan-600/10 text-cyan-300"
                        : "border-slate-700 bg-slate-900/30 text-foreground hover:border-slate-500"
                    }`}
                    data-testid={`builder-length-${opt.id}`}
                  >
                    <p className="text-xs font-medium">{opt.label}</p>
                    <p className="text-[10px] text-muted-foreground">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Story Arc Type</label>
              <Select value={arcType} onValueChange={setArcType}>
                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-sm" data-testid="builder-arc-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ARC_TYPES.map(a => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Card className="bg-slate-900/30 border-slate-700">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Estimated book:</strong>{" "}
                  {numChapters} chapters x{" "}
                  {chapterLength === "short" ? "~700" : chapterLength === "long" ? "~2,100" : "~1,200"} words ={" "}
                  <strong className="text-cyan-400">
                    ~{(numChapters * (chapterLength === "short" ? 700 : chapterLength === "long" ? 2100 : 1200)).toLocaleString()} words
                  </strong>
                </p>
              </CardContent>
            </Card>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(4)} className="flex-1" data-testid="builder-back-btn">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> World
              </Button>
              <Button onClick={generateOutlines} className="flex-1 bg-cyan-600 hover:bg-cyan-700" data-testid="builder-generate-outlines-btn">
                <Wand2 className="w-3.5 h-3.5 mr-1" /> Generate Chapters
              </Button>
            </div>
          </motion.div>
        )}

        {/* STEP 6: CHAPTER OUTLINES */}
        {step === 6 && (
          <motion.div key="chapters" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Chapter Outlines ({chapters.length})</h3>
            </div>

            {/* Tension curve visualization */}
            {chapters.length > 0 && (
              <div className="bg-slate-900/30 rounded-lg p-2 border border-slate-700">
                <p className="text-[10px] text-muted-foreground mb-1">Tension Curve</p>
                <div className="flex items-end gap-px h-12">
                  {chapters.map((ch, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-gradient-to-t from-cyan-600/30 to-cyan-500 rounded-t-sm transition-all hover:from-cyan-600/50"
                      style={{ height: `${(ch.tension_level || 5) * 10}%` }}
                      title={`Ch ${ch.number}: ${ch.title} (Tension: ${ch.tension_level}/10)`}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {chapters.map((ch, idx) => (
                <Card key={idx} className="bg-slate-900/40 border-slate-700">
                  <CardContent className="p-2.5">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedChapter(expandedChapter === idx ? null : idx)}
                      data-testid={`builder-chapter-${idx}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-cyan-500 w-6">{ch.number || idx + 1}</span>
                        <p className="text-xs font-medium text-foreground">{ch.title}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[9px]">T:{ch.tension_level || 5}/10</Badge>
                        {expandedChapter === idx ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                      </div>
                    </div>

                    {expandedChapter === idx && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="mt-2 space-y-2 border-t border-slate-700 pt-2">
                        <div>
                          <label className="text-[10px] text-muted-foreground">Title</label>
                          <Input value={ch.title || ""} onChange={(e) => updateChapter(idx, "title", e.target.value)} className="bg-slate-800 border-slate-600 text-xs h-8" />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">Summary</label>
                          <Textarea value={ch.summary || ""} onChange={(e) => updateChapter(idx, "summary", e.target.value)} className="bg-slate-800 border-slate-600 text-xs min-h-[60px]" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-muted-foreground">POV</label>
                            <Input value={ch.pov || ""} onChange={(e) => updateChapter(idx, "pov", e.target.value)} className="bg-slate-800 border-slate-600 text-xs h-8" />
                          </div>
                          <div>
                            <label className="text-[10px] text-muted-foreground">Setting</label>
                            <Input value={ch.setting || ""} onChange={(e) => updateChapter(idx, "setting", e.target.value)} className="bg-slate-800 border-slate-600 text-xs h-8" />
                          </div>
                        </div>
                        {ch.key_beats && (
                          <div>
                            <label className="text-[10px] text-muted-foreground">Key Beats</label>
                            <ul className="list-disc list-inside text-[11px] text-foreground/80 space-y-0.5 ml-1">
                              {(ch.key_beats || []).map((beat, bi) => (
                                <li key={bi}>{beat}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(5)} className="flex-1" data-testid="builder-back-btn">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Scope
              </Button>
              <Button onClick={() => setStep(7)} className="flex-1 bg-cyan-600 hover:bg-cyan-700" data-testid="builder-next-btn">
                Review <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* STEP 7: REVIEW & CREATE */}
        {step === 7 && (
          <motion.div key="review" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Review Your Book</h3>

            <Card className="bg-slate-900/30 border-cyan-500/30">
              <CardContent className="p-3 space-y-2">
                <h4 className="font-bold text-foreground">{foundation?.titles?.[selectedTitle] || "Untitled"}</h4>
                <div className="flex flex-wrap gap-1">
                  <Badge className="bg-cyan-600/20 text-cyan-300 text-[10px]">{foundation?.genre}</Badge>
                  <Badge variant="outline" className="text-[10px]">{foundation?.tone}</Badge>
                  <Badge variant="outline" className="text-[10px]">{numChapters} chapters</Badge>
                  <Badge variant="outline" className="text-[10px]">{chapterLength}</Badge>
                  <Badge variant="outline" className="text-[10px]">{arcType}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{foundation?.elevator_pitch}</p>
              </CardContent>
            </Card>

            <div className="grid grid-cols-3 gap-2">
              <Card className="bg-slate-900/30 border-slate-700">
                <CardContent className="p-2 text-center">
                  <p className="text-lg font-bold text-cyan-400">{characters.length}</p>
                  <p className="text-[10px] text-muted-foreground">Characters</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/30 border-slate-700">
                <CardContent className="p-2 text-center">
                  <p className="text-lg font-bold text-cyan-400">{chapters.length}</p>
                  <p className="text-[10px] text-muted-foreground">Chapters</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/30 border-slate-700">
                <CardContent className="p-2 text-center">
                  <p className="text-lg font-bold text-cyan-400">
                    ~{(numChapters * (chapterLength === "short" ? 700 : chapterLength === "long" ? 2100 : 1200) / 1000).toFixed(0)}k
                  </p>
                  <p className="text-[10px] text-muted-foreground">Est. Words</p>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-1 max-h-40 overflow-y-auto">
              {characters.slice(0, 5).map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${
                    c.role === 'protagonist' ? 'bg-cyan-600/30 text-cyan-300' :
                    c.role === 'antagonist' ? 'bg-red-600/30 text-red-300' :
                    'bg-slate-700 text-slate-300'
                  }`}>{(c.name || "?")[0]}</div>
                  <span className="text-foreground">{c.name}</span>
                  <Badge variant="outline" className="text-[9px]">{c.role}</Badge>
                </div>
              ))}
              {characters.length > 5 && <p className="text-[10px] text-muted-foreground">+ {characters.length - 5} more</p>}
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(6)} className="flex-1" data-testid="builder-back-btn">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Chapters
              </Button>
              <Button onClick={handleCreateProject} className="flex-1 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700" data-testid="builder-create-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                Create My Book
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StoryBuilderWizard;
